import * as Yup from "yup";

/**
 * Password policy for the Set / Reset Password screen (ECRH-5171).
 *
 * Each rule is validated in real time as the user types so the UI can show a
 * per-rule pass/fail state and an overall strength indicator.
 */

export type PasswordRule = {
  /** Stable key used as a React list key. */
  key: string;
  /** User-facing requirement copy. */
  label: string;
  /** Returns true when the given password satisfies the rule. */
  test: (password: string) => boolean;
};

export const PASSWORD_RULES: PasswordRule[] = [
  {
    key: "length",
    label: "At least 8 characters",
    test: (p) => p.length >= 8,
  },
  {
    key: "uppercase",
    label: "At least one uppercase letter (A–Z)",
    test: (p) => /[A-Z]/.test(p),
  },
  {
    key: "lowercase",
    label: "At least one lowercase letter (a–z)",
    test: (p) => /[a-z]/.test(p),
  },
  {
    key: "number",
    label: "At least one number (0–9)",
    test: (p) => /[0-9]/.test(p),
  },
  {
    key: "special",
    label: "At least one special character (!@#$%&* etc.)",
    test: (p) => /[^A-Za-z0-9]/.test(p),
  },
];

/** How many policy rules the password currently satisfies. */
export const countSatisfiedRules = (password: string): number =>
  PASSWORD_RULES.reduce((n, rule) => (rule.test(password) ? n + 1 : n), 0);

/** True only when every policy rule is satisfied. */
export const isPasswordValid = (password: string): boolean =>
  countSatisfiedRules(password) === PASSWORD_RULES.length;

export type PasswordStrength = "Weak" | "Medium" | "Strong";

/**
 * Derives a coarse strength label from the number of satisfied rules.
 * An empty password has no strength (null) so the meter can stay hidden.
 */
export const getPasswordStrength = (
  password: string
): PasswordStrength | null => {
  if (!password) return null;
  const satisfied = countSatisfiedRules(password);
  if (satisfied <= 2) return "Weak";
  if (satisfied <= 4) return "Medium";
  return "Strong";
};

/**
 * Yup validation for the Set / Reset Password form. Enforces the full policy on
 * `newPassword` and an exact match on `confirmPassword`.
 */
export const setPasswordValidationSchema = Yup.object({
  newPassword: Yup.string()
    .required("New Password is required")
    .test(
      "password-policy",
      "Password does not meet all requirements",
      (value) => isPasswordValid(value ?? "")
    )
    .label("New Password"),
  confirmPassword: Yup.string()
    .required("Confirm Password is required")
    .oneOf([Yup.ref("newPassword"), ""], "Passwords must match")
    .label("Confirm Password"),
});
